import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import './index.css';
import App from './App';
import { msalConfig, isMsalConfigured } from './authConfig';
import reportWebVitals from './reportWebVitals';

// ── Bootstrap MSAL ──────────────────────────────────────────────────────────
// We must call initialize() before rendering anything so that:
//   - cached accounts are loaded from sessionStorage
//   - any in-flight redirect callback (after returning from login.microsoftonline.com)
//     is processed and the token is stored
//
// Then we attach an event callback so future logins set the active account
// automatically.
const msalInstance = new PublicClientApplication(msalConfig);

function renderApp() {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </React.StrictMode>
  );
}

if (!isMsalConfigured) {
  // Render a clear error instead of confusing MSAL stack traces
  console.error(
    '[auth] REACT_APP_AAD_TENANT_ID and/or REACT_APP_AAD_CLIENT_ID are not set. ' +
    'Local: check .env. Production: check pipeline + Key Vault.'
  );
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <div style={{ padding: 24, fontFamily: 'sans-serif', color: '#b00020' }}>
      <h2>SSO not configured</h2>
      <p><code>REACT_APP_AAD_TENANT_ID</code> or <code>REACT_APP_AAD_CLIENT_ID</code> is missing.</p>
      <p>Local dev: set these in <code>.env</code> and restart <code>npm start</code>.</p>
    </div>
  );
} else {
  msalInstance.initialize().then(() => {
    // If we have any cached accounts, pick the first as the active one.
    // This is what makes "remembered" sign-ins seamless on page reload.
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    // On successful interactive login (after redirect callback returns),
    // set the new account as active so the rest of the app sees them.
    msalInstance.addEventCallback((event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
         event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload?.account
      ) {
        msalInstance.setActiveAccount(event.payload.account);
      }
    });

    renderApp();
  }).catch((err) => {
    console.error('[auth] MSAL initialize failed:', err);
    renderApp(); // render anyway — App.js will show the unauthenticated state
  });
}

reportWebVitals();
